/**
 * Unified Wallet Hook
 *
 * Single source of truth for wallet address, connection state, and provider
 * across Phantom embedded wallet, AppKit standard wallets, and direct Gate Wallet adapter.
 *
 * This abstraction ensures all data-fetching hooks and components
 * work correctly regardless of which wallet provider is active.
 */

import { useState, useEffect } from 'react';
import { useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';

import { usePhantom, useIsPhantomActive } from '@/context/PhantomContext';
import { getGateWalletAdapter } from '@/adapters/GateWalletAdapter';

export function useWalletAddress() {
  // Get AppKit wallet state
  const { address: appKitAddress, isConnected: appKitConnected } = useAppKitAccount();
  const { walletProvider: appKitProvider } = useAppKitProvider('solana');

  // Get Phantom wallet state
  const {
    phantomAddress,
    isPhantomConnected,
    isEmbeddedWallet,
    signPhantomTransaction,
    signAllPhantomTransactions,
    signAndSendPhantomTransaction,
    phantomPublicKey
  } = usePhantom();
  const isPhantomConnectedFromSDK = useIsPhantomActive();

  // Track direct Gate Wallet adapter connections (outside of AppKit)
  const [gateWalletState, setGateWalletState] = useState<{
    address: string | null;
    isConnected: boolean;
  }>({ address: null, isConnected: false });

  useEffect(() => {
    const adapter = getGateWalletAdapter();

    const updateGateState = () => {
      const connected = adapter.connected && !!adapter.publicKey;
      setGateWalletState({
        address: connected ? adapter.publicKey?.toBase58() ?? null : null,
        isConnected: connected,
      });
    };

    // Initial check
    updateGateState();

    // Listen for connect/disconnect events
    const onConnect = () => updateGateState();
    const onDisconnect = () => setGateWalletState({ address: null, isConnected: false });

    adapter.on('connect', onConnect);
    adapter.on('disconnect', onDisconnect);

    return () => {
      adapter.off('connect', onConnect);
      adapter.off('disconnect', onDisconnect);
    };
  }, []);

  // Gate Wallet direct connection is only "active" when connected AND AppKit is NOT connected
  const isGateDirectActive = gateWalletState.isConnected && !appKitConnected;

  // Phantom is only "active" when it's connected AND AppKit/Gate are NOT connected.
  // This ensures WalletConnect users are never routed through Phantom's signing methods,
  // which would fail with "embedded wallet" errors for non-Phantom wallets.
  // When both have connection state (e.g., cached Phantom session + new WalletConnect),
  // we prioritize AppKit since the user explicitly chose to connect via WalletConnect.
  const isPhantomActive = isPhantomConnectedFromSDK && !appKitConnected && !isGateDirectActive;

  // Determine which wallet is active and return its details
  // Priority: AppKit > Gate Direct > Phantom
  const address = appKitConnected
    ? appKitAddress
    : isGateDirectActive
    ? gateWalletState.address
    : isPhantomActive
    ? phantomAddress
    : appKitAddress;
  const isConnected = appKitConnected || isGateDirectActive || isPhantomActive;

  // Get the Gate adapter for building wallet provider
  const gateAdapter = getGateWalletAdapter();

  // Create a unified wallet provider interface
  // Note: We need to explicitly bind methods from appKitProvider because spreading
  // only copies own enumerable properties, not prototype methods like signMessage
  const appKitProviderAny = appKitProvider as any;
  const walletProvider = isPhantomActive
    ? {
        // Phantom provider wrapper
        signTransaction: signPhantomTransaction,
        signAllTransactions: signAllPhantomTransactions,
        signAndSendTransaction: signAndSendPhantomTransaction,
        publicKey: phantomPublicKey,
        isPhantom: true,
        isEmbeddedWallet: isEmbeddedWallet,
      }
    : isGateDirectActive
    ? {
        // Gate Wallet adapter wrapper
        signTransaction: gateAdapter.signTransaction.bind(gateAdapter),
        signAllTransactions: gateAdapter.signAllTransactions.bind(gateAdapter),
        signMessage: gateAdapter.signMessage.bind(gateAdapter),
        publicKey: gateAdapter.publicKey,
        isGateWallet: true,
        isEmbeddedWallet: false,
      }
    : {
        // AppKit provider with embedded wallet flags set to false
        // Spread own properties
        ...appKitProvider,
        // Explicitly bind prototype methods that don't get spread
        signMessage: appKitProviderAny?.signMessage?.bind(appKitProvider),
        signTransaction: appKitProviderAny?.signTransaction?.bind(appKitProvider),
        signAllTransactions: appKitProviderAny?.signAllTransactions?.bind(appKitProvider),
        sendTransaction: appKitProviderAny?.sendTransaction?.bind(appKitProvider),
        isEmbeddedWallet: false,
      };

  return {
    address,
    isConnected,
    walletProvider,
    // Additional metadata for debugging
    walletType: isPhantomActive ? 'phantom' : isGateDirectActive ? 'gate' : appKitConnected ? 'appkit' : null,
    isPhantomActive,
    isGateDirectActive,
  };
}